/**
 * BillingSuccess — Pagina di ritorno post-checkout (Stripe + PayPal).
 *
 * URL query params possibili:
 *   ?provider=stripe&session_id=...                 (Stripe subscription o lifetime)
 *   ?provider=stripe&session_id=...&product=lifetime
 *   ?provider=paypal&kind=subscription              (PayPal subscription approvata)
 *   ?provider=paypal&kind=lifetime&token=ORDER_ID   (PayPal one-shot da catturare)
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Loader, Crown } from "lucide-react";
import { api } from "@/lib/api";

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const provider = params.get("provider") || "stripe";
    const kind = params.get("kind") || (params.get("product") === "lifetime" ? "lifetime" : "subscription");
    const sessionId = params.get("session_id");
    // PayPal mette il token (=Order ID) nei params al ritorno
    const paypalOrderId = params.get("token") || params.get("order_id");
    const widgetToken = params.get("widget_token");

    const [status, setStatus] = useState("polling");  // polling | paid | failed
    const [attempts, setAttempts] = useState(0);
    const [founderNumber, setFounderNumber] = useState(null);

    const isLifetime = kind === "lifetime";

    useEffect(() => {
        document.title = "Pagamento confermato — TotalKlick";
    }, []);

    useEffect(() => {
        let timer;
        let cancelled = false;
        const safeSet = (fn) => { if (!cancelled) fn(); };

        // PayPal Lifetime → bisogna fare CAPTURE dell'order
        if (provider === "paypal" && isLifetime && paypalOrderId) {
            (async () => {
                try {
                    const r = await api.post(`/billing/paypal/lifetime/capture/${paypalOrderId}`);
                    safeSet(() => {
                        if (r.data.status === "ok") {
                            setStatus("paid");
                            setFounderNumber(r.data.founder_number || null);
                        } else {
                            setStatus("failed");
                        }
                    });
                } catch (_e) {
                    safeSet(() => setStatus("failed"));
                }
            })();
            return () => { cancelled = true; };
        }

        // PayPal Subscription → il webhook attiva tutto; mostriamo conferma diretta
        if (provider === "paypal" && !isLifetime) {
            const pollMe = async (i = 0) => {
                try {
                    const r = await api.get("/billing/me");
                    const st = r.data.status;
                    if (st === "trialing" || st === "active") {
                        safeSet(() => setStatus("paid"));
                        return;
                    }
                    if (i >= 8) { safeSet(() => setStatus("paid")); return; }
                    safeSet(() => setAttempts(i + 1));
                    timer = setTimeout(() => pollMe(i + 1), 1500);
                } catch (_e) {
                    if (i >= 8) { safeSet(() => setStatus("paid")); return; }
                    timer = setTimeout(() => pollMe(i + 1), 1500);
                }
            };
            pollMe();
            return () => { cancelled = true; clearTimeout(timer); };
        }

        // Stripe (default) — polling sulla session
        if (provider === "stripe") {
            if (!sessionId) { safeSet(() => setStatus("failed")); return; }
            const pollStripe = async (i = 0) => {
                try {
                    const res = await api.get(`/billing/stripe/checkout-status/${sessionId}`);
                    const ps = res.data.payment_status;
                    // Subscription: payment_status puo essere "no_payment_required" durante il trial
                    // Lifetime: payment_status === "paid"
                    if (ps === "paid" || ps === "no_payment_required") {
                        safeSet(() => setStatus("paid"));
                        if (isLifetime) {
                            try {
                                const me = await api.get("/billing/me");
                                if (me.data.founder_number) safeSet(() => setFounderNumber(me.data.founder_number));
                            } catch (_e) {
                                // ignore
                            }
                        }
                        return;
                    }
                    if (i >= 10) { safeSet(() => setStatus("failed")); return; }
                    safeSet(() => setAttempts(i + 1));
                    timer = setTimeout(() => pollStripe(i + 1), 1500);
                } catch (_e) {
                    if (i >= 10) { safeSet(() => setStatus("failed")); return; }
                    timer = setTimeout(() => pollStripe(i + 1), 1500);
                }
            };
            pollStripe();
            return () => { cancelled = true; clearTimeout(timer); };
        }

        safeSet(() => setStatus("failed"));
        return () => { cancelled = true; };
    }, [provider, sessionId, paypalOrderId, isLifetime]);

    const accent = isLifetime
        ? { c1: "#FFD66B", c2: "#FF6B3D", glow: "rgba(255,214,107,0.45)" }
        : { c1: "#86EFAC", c2: "#10B981", glow: "rgba(134,239,172,0.4)" };

    return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="billing-success-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-md w-full rounded-3xl p-8 text-center"
                style={{ background: "rgba(15,18,28,0.7)", border: `1px solid ${accent.c1}55`, backdropFilter: "blur(20px)", boxShadow: `0 30px 80px -20px ${accent.glow}` }}
            >
                {status === "polling" && (
                    <>
                        <Loader size={36} className="text-[#9EC3FF] mx-auto mb-4 animate-spin" />
                        <h1 className="text-white text-[20px] mb-2" style={{ fontWeight: 500 }}>Stiamo confermando il pagamento…</h1>
                        <p className="text-white/65 text-[14px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Tentativo {attempts + 1} di 11 · ci vogliono pochi secondi
                        </p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        {isLifetime ? (
                            <Crown size={48} style={{ color: accent.c1 }} className="mx-auto mb-4" />
                        ) : (
                            <CheckCircle2 size={48} style={{ color: accent.c1 }} className="mx-auto mb-4" />
                        )}
                        <h1 className="text-white text-[24px] mb-2" style={{ fontWeight: 400, letterSpacing: "-0.02em" }}>
                            {isLifetime ? "Benvenuto nel Founders Club" : "Benvenuto in Aria"}
                        </h1>
                        {isLifetime && founderNumber && (
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 rounded-full text-[11px] uppercase tracking-[0.2em]" style={{ background: "rgba(255,214,107,0.15)", color: "#FFD66B", border: "1px solid rgba(255,214,107,0.4)", fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }} data-testid="founder-number">
                                Founder #{String(founderNumber).padStart(3, "0")}
                            </div>
                        )}
                        <p className="text-white/75 text-[14px] mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {isLifetime
                                ? "Riceverai via email il certificato firmato + la lettera d'intenti sul diritto di prelazione equity."
                                : "Il tuo trial di 15 giorni è iniziato. Riceverai conferma via email entro pochi minuti."}
                        </p>
                        <Link
                            to={widgetToken && !isLifetime ? `/aria-widget/config/${widgetToken}` : "/app/aria"}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-[13.5px] transition hover:scale-[1.03]"
                            style={{ background: `linear-gradient(135deg, ${accent.c1}, ${accent.c2})`, fontFamily: "Geist, sans-serif", fontWeight: 500, color: isLifetime ? "#1a0c00" : "#fff" }}
                            data-testid="success-to-dashboard"
                        >
                            Vai alla tua Aria <ArrowRight size={13} />
                        </Link>
                    </>
                )}
                {status === "failed" && (
                    <>
                        <h1 className="text-white text-[20px] mb-2" style={{ fontWeight: 500 }}>Non riusciamo a confermare</h1>
                        <p className="text-white/65 text-[14px] mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Se hai completato il pagamento, riceverai conferma via email. Altrimenti riprova dalla pagina piani.
                        </p>
                        <Link to="/aria/piani" className="text-[13px] text-white/85 hover:text-white" data-testid="success-back">
                            Torna ai piani
                        </Link>
                    </>
                )}
            </motion.div>
        </div>
    );
}
